import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ContactPicker from '../common/ContactPicker';
import { X, User, Mail, Phone, MapPin, Briefcase, FileText, Loader2, Calendar, AlertTriangle, Shield, Heart, Building2, MessageSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const MARITAL_STATUSES = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
  { value: 'separated', label: 'Separated' }
];

const CONTACT_METHODS = [
  { value: 'phone', label: 'Phone Call' },
  { value: 'email', label: 'Email' },
  { value: 'text', label: 'Text Message' }
];

function FullIntakeModal({ isOpen, onClose, onSuccess, intake = null }) {
  const [loading, setLoading] = useState(false);
  const [sources, setSources] = useState([]);
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);

  // Track if we're editing an existing intake
  const isEditing = !!intake?.id;

  const [linkedContact, setLinkedContact] = useState(null); // returning client
  const [formData, setFormData] = useState({
    // Personal Info
    prefix: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    suffix: '',
    date_of_birth: '',

    // Identification
    ssn: '',
    drivers_license_number: '',
    drivers_license_state: '',

    // Marital/Household
    marital_status: '',
    spouse_name: '',

    // Emergency Contact
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',

    // Employment
    employer_name: '',
    employer_address: '',
    employer_phone: '',
    occupation: '',

    // Communication
    preferred_contact_method: 'phone',

    // Contact Info
    primary_phone: '',
    secondary_phone: '',
    email: '',

    // Address
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    address_country: 'United States',

    // Intake Details
    source_id: '',
    source_details: '',
    referral_name: '',
    practice_area: '',
    matter_description: '',
    urgency: 'normal',

    // Assignment
    assigned_to: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchMetadata();
      setLinkedContact(null);
      if (intake) {
        // Pre-populate form with existing intake data
        setFormData({
          prefix: intake.prefix || '',
          first_name: intake.first_name || '',
          middle_name: intake.middle_name || '',
          last_name: intake.last_name || '',
          suffix: intake.suffix || '',
          date_of_birth: intake.date_of_birth || '',
          ssn: intake.ssn || '',
          drivers_license_number: intake.drivers_license_number || '',
          drivers_license_state: intake.drivers_license_state || '',
          marital_status: intake.marital_status || '',
          spouse_name: intake.spouse_name || '',
          emergency_contact_name: intake.emergency_contact_name || '',
          emergency_contact_phone: intake.emergency_contact_phone || '',
          emergency_contact_relationship: intake.emergency_contact_relationship || '',
          employer_name: intake.employer_name || '',
          employer_address: intake.employer_address || '',
          employer_phone: intake.employer_phone || '',
          occupation: intake.occupation || '',
          preferred_contact_method: intake.preferred_contact_method || 'phone',
          primary_phone: intake.primary_phone || '',
          secondary_phone: intake.secondary_phone || '',
          email: intake.email || '',
          address_street: intake.address_street || '',
          address_city: intake.address_city || '',
          address_state: intake.address_state || '',
          address_zip: intake.address_zip || '',
          address_country: intake.address_country || 'United States',
          source_id: intake.source_id || '',
          source_details: intake.source_details || '',
          referral_name: intake.referral_name || '',
          practice_area: intake.practice_area || '',
          matter_description: intake.matter_description || '',
          urgency: intake.urgency || 'normal',
          assigned_to: intake.assigned_to || ''
        });
      } else {
        resetForm();
      }
    }
  }, [isOpen, intake]);

  const resetForm = () => {
    setFormData({
      prefix: '',
      first_name: '',
      middle_name: '',
      last_name: '',
      suffix: '',
      date_of_birth: '',
      ssn: '',
      drivers_license_number: '',
      drivers_license_state: '',
      marital_status: '',
      spouse_name: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      emergency_contact_relationship: '',
      employer_name: '',
      employer_address: '',
      employer_phone: '',
      occupation: '',
      preferred_contact_method: 'phone',
      primary_phone: '',
      secondary_phone: '',
      email: '',
      address_street: '',
      address_city: '',
      address_state: '',
      address_zip: '',
      address_country: 'United States',
      source_id: '',
      source_details: '',
      referral_name: '',
      practice_area: '',
      matter_description: '',
      urgency: 'normal',
      assigned_to: ''
    });
    setError(null);
  };

  const fetchMetadata = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [sourcesRes, paRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/intake/sources`, { headers }),
        axios.get(`${API_URL}/intake/practice-areas`, { headers }),
        axios.get(`${API_URL}/admin/users`, { headers }).catch(() => ({ data: [] }))
      ]);

      setSources(sourcesRes.data || []);
      setPracticeAreas(paRes.data || []);
      setUsers(usersRes.data || []);
    } catch (err) {
      console.error('Error fetching metadata:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');

      // Build the payload, excluding empty strings
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name
      };
      if (linkedContact?.contact_id) payload.contact_id = linkedContact.contact_id;

      // Add optional fields only if they have values
      if (formData.prefix) payload.prefix = formData.prefix;
      if (formData.middle_name) payload.middle_name = formData.middle_name;
      if (formData.suffix) payload.suffix = formData.suffix;
      if (formData.date_of_birth) payload.date_of_birth = formData.date_of_birth;

      // Identification
      if (formData.ssn) payload.ssn = formData.ssn;
      if (formData.drivers_license_number) payload.drivers_license_number = formData.drivers_license_number;
      if (formData.drivers_license_state) payload.drivers_license_state = formData.drivers_license_state;

      // Marital/Household
      if (formData.marital_status) payload.marital_status = formData.marital_status;
      if (formData.spouse_name) payload.spouse_name = formData.spouse_name;

      // Emergency Contact
      if (formData.emergency_contact_name) payload.emergency_contact_name = formData.emergency_contact_name;
      if (formData.emergency_contact_phone) payload.emergency_contact_phone = formData.emergency_contact_phone;
      if (formData.emergency_contact_relationship) payload.emergency_contact_relationship = formData.emergency_contact_relationship;

      // Employment
      if (formData.employer_name) payload.employer_name = formData.employer_name;
      if (formData.employer_address) payload.employer_address = formData.employer_address;
      if (formData.employer_phone) payload.employer_phone = formData.employer_phone;
      if (formData.occupation) payload.occupation = formData.occupation;

      // Communication
      if (formData.preferred_contact_method) payload.preferred_contact_method = formData.preferred_contact_method;

      // Contact Info
      if (formData.primary_phone) payload.primary_phone = formData.primary_phone;
      if (formData.secondary_phone) payload.secondary_phone = formData.secondary_phone;
      if (formData.email) payload.email = formData.email;

      // Address
      if (formData.address_street) payload.address_street = formData.address_street;
      if (formData.address_city) payload.address_city = formData.address_city;
      if (formData.address_state) payload.address_state = formData.address_state;
      if (formData.address_zip) payload.address_zip = formData.address_zip;
      if (formData.address_country) payload.address_country = formData.address_country;

      // Intake Details
      if (formData.source_id) payload.source_id = parseInt(formData.source_id);
      if (formData.source_details) payload.source_details = formData.source_details;
      if (formData.referral_name) payload.referral_name = formData.referral_name;
      if (formData.practice_area) payload.practice_area = formData.practice_area;
      if (formData.matter_description) payload.matter_description = formData.matter_description;
      if (formData.urgency) payload.urgency = formData.urgency;
      if (formData.assigned_to) payload.assigned_to = parseInt(formData.assigned_to);

      let response;
      if (isEditing) {
        // Update existing intake
        response = await axios.put(`${API_URL}/intake/${intake.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new intake
        response = await axios.post(`${API_URL}/intake`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (onSuccess) {
        onSuccess(response.data);
      }
      onClose();
    } catch (err) {
      console.error('Error saving intake:', err);
      setError(err.response?.data?.detail || `Failed to ${isEditing ? 'update' : 'create'} intake. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-virgo-navy-700 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-virgo-navy-600 sticky top-0 bg-virgo-navy-700 z-10">
          <h2 className="text-xl font-semibold text-white">
            {isEditing ? 'Edit Intake' : 'New Intake'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-virgo-navy-600 rounded"
          >
            <X className="w-5 h-5 text-virgo-teal-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {/* Personal Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-virgo-teal-400 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Personal Information
            </h3>
            <div className="grid grid-cols-6 gap-4">
              <div className="col-span-1">
                <label className="block text-sm text-virgo-teal-400 mb-1">Prefix</label>
                <select
                  name="prefix"
                  value={formData.prefix}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                >
                  <option value="">--</option>
                  <option value="Mr.">Mr.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Ms.">Ms.</option>
                  <option value="Dr.">Dr.</option>
                </select>
              </div>
              <div className="col-span-6">
                {linkedContact ? (
                  <div className="flex items-center justify-between rounded-lg border border-virgo-gold-500/40 bg-virgo-gold-500/10 px-3 py-2">
                    <span className="text-sm text-virgo-gold-200">Returning client: <strong>{linkedContact.name}</strong></span>
                    <button type="button" onClick={() => setLinkedContact(null)}
                      className="text-xs text-virgo-teal-400 hover:text-white underline">unlink</button>
                  </div>
                ) : (
                  <>
                    <label className="block text-sm text-virgo-teal-400 mb-1">Returning client? Search to prefill and link</label>
                    <ContactPicker
                      onSelect={(c) => {
                        setLinkedContact(c);
                        setFormData((f) => ({
                          ...f,
                          first_name: c.first_name || f.first_name,
                          last_name: c.last_name || f.last_name,
                          email: c.email || f.email,
                          primary_phone: c.phone || f.primary_phone,
                        }));
                      }}
                    />
                  </>
                )}
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-virgo-teal-400 mb-1">First Name *</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm text-virgo-teal-400 mb-1">Middle</label>
                <input
                  type="text"
                  name="middle_name"
                  value={formData.middle_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-virgo-teal-400 mb-1">Last Name *</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 mt-4">
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Suffix</label>
                <select
                  name="suffix"
                  value={formData.suffix}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                >
                  <option value="">--</option>
                  <option value="Jr.">Jr.</option>
                  <option value="Sr.">Sr.</option>
                  <option value="II">II</option>
                  <option value="III">III</option>
                  <option value="IV">IV</option>
                  <option value="Esq.">Esq.</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-virgo-teal-400 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
            </div>
          </div>

          {/* Identification */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-virgo-teal-400 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Identification
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">
                  Social Security Number *
                </label>
                <input
                  type="text"
                  name="ssn"
                  value={formData.ssn}
                  onChange={handleChange}
                  placeholder="XXX-XX-XXXX"
                  maxLength={11}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Driver's License #</label>
                <input
                  type="text"
                  name="drivers_license_number"
                  value={formData.drivers_license_number}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">DL State</label>
                <select
                  name="drivers_license_state"
                  value={formData.drivers_license_state}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                >
                  <option value="">Select...</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Marital/Household Status */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-virgo-teal-400 mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5" />
              Household Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Marital Status</label>
                <select
                  name="marital_status"
                  value={formData.marital_status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                >
                  <option value="">Select...</option>
                  {MARITAL_STATUSES.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Spouse Name</label>
                <input
                  type="text"
                  name="spouse_name"
                  value={formData.spouse_name}
                  onChange={handleChange}
                  placeholder="If married"
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-virgo-teal-400 mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Emergency Contact
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Contact Name</label>
                <input
                  type="text"
                  name="emergency_contact_name"
                  value={formData.emergency_contact_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Phone Number</label>
                <input
                  type="tel"
                  name="emergency_contact_phone"
                  value={formData.emergency_contact_phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Relationship</label>
                <input
                  type="text"
                  name="emergency_contact_relationship"
                  value={formData.emergency_contact_relationship}
                  onChange={handleChange}
                  placeholder="e.g., Spouse, Parent"
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
            </div>
          </div>

          {/* Employment Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-virgo-teal-400 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Employment Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Employer Name</label>
                <input
                  type="text"
                  name="employer_name"
                  value={formData.employer_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Occupation/Title</label>
                <input
                  type="text"
                  name="occupation"
                  value={formData.occupation}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Employer Address</label>
                <input
                  type="text"
                  name="employer_address"
                  value={formData.employer_address}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Employer Phone</label>
                <input
                  type="tel"
                  name="employer_phone"
                  value={formData.employer_phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
            </div>
          </div>

          {/* Communication Preferences */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-virgo-teal-400 mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Communication Preferences
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Preferred Contact Method</label>
                <select
                  name="preferred_contact_method"
                  value={formData.preferred_contact_method}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                >
                  {CONTACT_METHODS.map(method => (
                    <option key={method.value} value={method.value}>{method.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-virgo-teal-400 mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Contact Information
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Primary Phone
                </label>
                <input
                  type="tel"
                  name="primary_phone"
                  value={formData.primary_phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Secondary Phone</label>
                <input
                  type="tel"
                  name="secondary_phone"
                  value={formData.secondary_phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email
                </label>
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-virgo-teal-400 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Address
            </h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Street Address</label>
                <input
                  type="text"
                  name="address_street"
                  value={formData.address_street}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 mt-4">
              <div className="col-span-2">
                <label className="block text-sm text-virgo-teal-400 mb-1">City</label>
                <input
                  type="text"
                  name="address_city"
                  value={formData.address_city}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">State</label>
                <select
                  name="address_state"
                  value={formData.address_state}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                >
                  <option value="">Select...</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">ZIP Code</label>
                <input
                  type="text"
                  name="address_zip"
                  value={formData.address_zip}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
            </div>
          </div>

          {/* Intake Details */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-virgo-teal-400 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Intake Details
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Source</label>
                <select
                  name="source_id"
                  value={formData.source_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                >
                  <option value="">Select...</option>
                  {sources.map(source => (
                    <option key={source.id} value={source.id}>{source.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Practice Area</label>
                <select
                  name="practice_area"
                  value={formData.practice_area}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                >
                  <option value="">Select...</option>
                  {practiceAreas.map((pa, i) => (
                    <option key={i} value={pa}>{pa}</option>
                  ))}
                  <option value="Family Law">Family Law</option>
                  <option value="Criminal Defense">Criminal Defense</option>
                  <option value="Personal Injury">Personal Injury</option>
                  <option value="Business Law">Business Law</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Estate Planning">Estate Planning</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Urgency</label>
                <select
                  name="urgency"
                  value={formData.urgency}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="critical">Urgent</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Source Details</label>
                <input
                  type="text"
                  name="source_details"
                  value={formData.source_details}
                  onChange={handleChange}
                  placeholder="Additional source info..."
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Referral Name</label>
                <input
                  type="text"
                  name="referral_name"
                  value={formData.referral_name}
                  onChange={handleChange}
                  placeholder="Who referred them?"
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm text-virgo-teal-400 mb-1">
                <FileText className="w-4 h-4 inline mr-1" />
                Matter Description
              </label>
              <textarea
                name="matter_description"
                value={formData.matter_description}
                onChange={handleChange}
                rows={4}
                placeholder="Brief description of the legal matter..."
                className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>
          </div>

          {/* Assignment */}
          {users.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-virgo-teal-400 mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Assignment
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-virgo-teal-400 mb-1">Assigned To</label>
                  <select
                    name="assigned_to"
                    value={formData.assigned_to}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500"
                  >
                    <option value="">Select...</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.full_name || user.username}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-virgo-navy-600">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-virgo-navy-500 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Intake'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FullIntakeModal;
